import { useMemo, useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight, RefreshCw, Plus, X } from "lucide-react";
import { addDays, format } from "date-fns";
import { it } from "date-fns/locale";
import { DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { useStore, moveTaskSmart, allCategories } from "@/lib/store";
import WeekGrid from "@/components/WeekGrid";
import TaskDialog from "@/components/TaskDialog";
import RecurrenceScopeDialog from "@/components/RecurrenceScopeDialog";
import AddTaskForm from "@/components/AddTaskForm";
import TimerBar from "@/components/TimerBar";
import { weekStart, weekRangeLabel, ISO } from "@/lib/date";
import { pickForSeed } from "@/lib/quotes";

export default function WeekPage() {
  const store = useStore();
  const [anchor, setAnchor] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogDefaults, setDialogDefaults] = useState(null);
  const [quoteSeed, setQuoteSeed] = useState(0);
  const [filterCat, setFilterCat] = useState("all");
  const [showAdd, setShowAdd] = useState(false);

  const start = weekStart(anchor);
  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(start, i)), [start]);
  const quote = useMemo(
    () => pickForSeed(store.settings.quotes, ISO(start) + "|w|" + quoteSeed),
    [store.settings.quotes, start, quoteSeed]
  );

  // Mobile: the grid lives in a full-screen shell under the app header, so day names
  // and hours stay put and the page itself never scrolls (no rubber-band bounce).
  const [mobile, setMobile] = useState(false);
  const [headerH, setHeaderH] = useState(56);
  useEffect(() => {
    function measure() {
      setMobile(window.innerWidth < 768);
      setHeaderH(document.querySelector('[data-testid="app-header"]')?.offsetHeight || 56);
    }
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, []);
  useEffect(() => {
    if (!mobile) return;
    window.scrollTo(0, 0);
    document.documentElement.classList.add("tl-lock");
    return () => document.documentElement.classList.remove("tl-lock");
  }, [mobile]);

  // Mobile swipe indicator: track which day is first-visible in the horizontal scroller.
  const scrollerRef = useRef(null);
  const [firstVisibleIdx, setFirstVisibleIdx] = useState(0);
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const first = el.querySelector('[data-testid^="week-col-"]');
        const colW = first ? first.getBoundingClientRect().width : 100;
        setFirstVisibleIdx(Math.max(0, Math.min(6, Math.round(el.scrollLeft / colW))));
      });
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => { el.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, [start, mobile]);

  function scrollToDay(idx) {
    const el = scrollerRef.current;
    if (!el) return;
    const first = el.querySelector('[data-testid^="week-col-"]');
    const colW = first ? first.getBoundingClientRect().width : 100;
    el.scrollTo({ left: idx * colW, behavior: "smooth" });
  }

  // First render: centre on today + current hour
  const didInitScroll = useRef(false);
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || didInitScroll.current) return;
    didInitScroll.current = true;
    const gridStartHour = store.settings.gridStart || 6;
    const now = new Date();
    const top = Math.max(0, (now.getHours() - gridStartHour - 1) * 56);
    const todayIdx = days.findIndex((d) => ISO(d) === ISO(now));
    const first = el.querySelector('[data-testid^="week-col-"]');
    const colW = first ? first.getBoundingClientRect().width : 100;
    el.scrollTo({ top, left: todayIdx > 0 ? todayIdx * colW : 0, behavior: "auto" });
  }, [days, store.settings.gridStart, mobile]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    // Touch: short tap = open/edit, long press = drag (so scrolling stays natural)
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 8 } })
  );
  const [pendingMove, setPendingMove] = useState(null);

  function onDragEnd(evt) {
    const { active, over } = evt;
    if (!over) return;
    const data = over.data.current;
    const task = active.data.current?.task;
    if (!task) return;
    const occurrenceDate = active.data.current?.occurrenceDate || task.date;
    let targetDate = null;
    let targetTime;
    if (data?.type === "slot") {
      targetDate = data.date;
      targetTime = data.time;
    } else if (data?.type === "day") {
      targetDate = data.date;
      targetTime = undefined;
    } else return;
    const unchanged = targetDate === occurrenceDate && (targetTime === undefined || targetTime === task.time);
    if (unchanged) return;
    if (task.recurrence && task.recurrence !== "none") {
      setPendingMove({ task, occurrenceDate, targetDate, targetTime });
      return;
    }
    moveTaskSmart(task, occurrenceDate, targetDate, targetTime, "single");
  }

  function openAt(date, time) {
    setDialogDefaults({ date, time });
    setDialogOpen(true);
  }

  const gridBlock = (
    <div ref={scrollerRef} className="tl-week-scroller overflow-auto flex-1 min-h-0">
      <div className="md:min-w-[900px]">
        <WeekGrid
          weekStart={start}
          gridStart={store.settings.gridStart || 6}
          gridEnd={store.settings.gridEnd || 23}
          onOpenTaskAt={openAt}
          filterCategory={filterCat}
        />
      </div>
    </div>
  );

  const swipeDots = (
    <div className="flex items-center justify-center gap-1.5 pt-1.5 flex-shrink-0" data-testid="week-swipe-dots" role="tablist" aria-label="Naviga i giorni">
      {days.map((d, i) => {
        const isActive = i >= firstVisibleIdx && i < firstVisibleIdx + 3;
        const isFirst = i === firstVisibleIdx;
        return (
          <button
            key={d.toISOString()}
            type="button"
            onClick={() => scrollToDay(i)}
            aria-label={`Vai a ${format(d, "EEEE d", { locale: it })}`}
            aria-current={isFirst ? "true" : undefined}
            data-testid={`week-dot-${i}`}
            data-active={isActive ? "true" : "false"}
            className={`transition-all duration-200 rounded-full ${isFirst ? "w-5 h-2 bg-[#8FA087]" : isActive ? "w-2 h-2 bg-[#A3B897]" : "w-2 h-2 bg-[#D5DBCF]"}`}
          />
        );
      })}
    </div>
  );

  const filterSelect = (extra) => (
    <select
      value={filterCat}
      onChange={(e) => setFilterCat(e.target.value)}
      data-testid="week-filter-category"
      className={`px-3 py-1.5 bg-white border border-[#E4E7E1] rounded-full text-sm text-[#3B443D] focus:outline-none focus:border-[#A3B897] ${extra || ""}`}
    >
      <option value="all">Tutte le categorie</option>
      {allCategories(store).map((c) => (
        <option key={c.id} value={c.id}>{c.name}</option>
      ))}
    </select>
  );

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      {mobile ? (
        <div
          className="fixed left-0 right-0 z-20 flex flex-col px-2 pb-1 bg-[#FDFDFB]"
          style={{ top: headerH, bottom: "var(--tl-tabbar-h)" }}
          data-testid="week-page"
        >
          <div className="flex items-center gap-1 flex-shrink-0">
            <button className="p-1.5 rounded-full tl-icon-btn bg-white flex-shrink-0" onClick={() => setAnchor(addDays(anchor, -7))} data-testid="week-prev">
              <ChevronLeft size={18} strokeWidth={1.75} />
            </button>
            <h1 className="font-serif-display font-semibold text-base text-[#3B443D] text-center leading-tight flex-1 min-w-0 truncate" data-testid="week-title">
              {weekRangeLabel(anchor)}
            </h1>
            <button className="p-1.5 rounded-full tl-icon-btn bg-white flex-shrink-0" onClick={() => setAnchor(addDays(anchor, 7))} data-testid="week-next">
              <ChevronRight size={18} strokeWidth={1.75} />
            </button>
          </div>

          <div className="flex items-center gap-2 py-1.5 flex-shrink-0">
            <button
              type="button"
              onClick={() => setAnchor(new Date())}
              className="tl-btn-soft px-3 py-1.5 rounded-full bg-white border border-[#E4E7E1] text-sm text-[#58635B] flex-shrink-0"
              data-testid="week-today"
            >
              Oggi
            </button>
            {filterSelect("flex-1 min-w-0")}
            <button
              type="button"
              onClick={() => setShowAdd((v) => !v)}
              className="tl-btn-primary px-3 py-1.5 rounded-full bg-[#A3B897] text-white text-sm inline-flex items-center gap-1 flex-shrink-0"
              data-testid="week-toggle-add"
            >
              {showAdd ? <X size={14} strokeWidth={2} /> : <Plus size={14} strokeWidth={2} />}
              {showAdd ? "Chiudi" : "Attività"}
            </button>
          </div>

          {showAdd && (
            <div className="pb-2 max-h-[46%] overflow-auto flex-shrink-0">
              <AddTaskForm defaultDate={null} testIdPrefix="week-add" compact />
            </div>
          )}

          <TimerBar />
          {gridBlock}
          {swipeDots}
        </div>
      ) : (
        <div className="flex flex-col gap-6" data-testid="week-page">
          <div className="flex flex-col items-center gap-3">
            <div className="flex items-center gap-3 w-full justify-center">
              <button className="p-2 rounded-full hover:bg-[#F3F5F0] tl-icon-btn bg-white flex-shrink-0" onClick={() => setAnchor(addDays(anchor, -7))} data-testid="week-prev">
                <ChevronLeft size={20} strokeWidth={1.75} />
              </button>
              <h1 className="font-serif-display font-semibold text-3xl md:text-5xl text-[#3B443D] text-center leading-tight min-w-0" data-testid="week-title">
                {weekRangeLabel(anchor)}
              </h1>
              <button className="p-2 rounded-full hover:bg-[#F3F5F0] tl-icon-btn bg-white flex-shrink-0" onClick={() => setAnchor(addDays(anchor, 7))} data-testid="week-next">
                <ChevronRight size={20} strokeWidth={1.75} />
              </button>
            </div>
            <div className="flex items-center gap-3 px-2 text-center">
              <div className="font-serif-display italic font-semibold text-2xl text-[#8FA087]" data-testid="week-quote">“{quote}”</div>
              <button className="text-[#6A7368] hover:text-[#8FA087] flex-shrink-0" onClick={() => setQuoteSeed((n) => n + 1)} aria-label="Cambia frase" data-testid="week-quote-refresh">
                <RefreshCw size={14} strokeWidth={1.5} />
              </button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 justify-between">
            <button
              type="button"
              onClick={() => setAnchor(new Date())}
              className="tl-btn-soft px-4 py-2 rounded-full bg-white border border-[#E4E7E1] text-sm text-[#58635B] hover:bg-[#F3F5F0]"
              data-testid="week-today"
            >
              Oggi
            </button>
            {filterSelect()}
          </div>

          <AddTaskForm defaultDate={null} testIdPrefix="week-add" />
          <TimerBar />
          {gridBlock}

          <div className="flex flex-wrap gap-3">
            {allCategories(store).map((c) => (
              <div key={c.id} className="tl-chip" style={{ background: c.color + "26", color: "#58635B" }}>
                <span className="tl-chip-dot" style={{ background: c.color }} />
                {c.name}
              </div>
            ))}
          </div>
        </div>
      )}

      <TaskDialog open={dialogOpen} onClose={() => setDialogOpen(false)} defaults={dialogDefaults} />
      <RecurrenceScopeDialog
        open={!!pendingMove}
        label="Spostare l'attività per..."
        onCancel={() => setPendingMove(null)}
        onChoose={(scope) => {
          const p = pendingMove;
          setPendingMove(null);
          if (p) moveTaskSmart(p.task, p.occurrenceDate, p.targetDate, p.targetTime, scope);
        }}
      />
    </DndContext>
  );
}
